import React, { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';

import useMerchantPaymentQuery from 'Hooks/useMerchantPaymentQuery';
import Urls from 'Containers/Urls';
import { LoggerFactory, DOMAIN, FEATURE } from 'Services/Logger';
import useCountDown from 'Redesign_Funnel/hooks/useCountDown';
import useIsEmailVerified from 'Utils/hooks/useIsEmailVerified';
import useCustomerLendingApplication from 'Hooks/useCustomerLendingApplication';
import useProtectedApplication from 'Hooks/useProtectedApplication';
import { useLocalStorage } from 'Hooks/useStorage';

import { EMAIL_VERIFICATION_STATUS, EMAIL_VERIFICATION_STATUS_VERIFIED } from '../EmailVerification.constants';
import { SendEmailUI } from '../components/SendEmailUI';
import { ResendEmailVerificationProps } from './ResendEmailVerification.types';
import { useSendEmailVerification } from './hooks/useSendEmailVerification';
import { useTrackSendEmailVerification } from './hooks/useTrackSendEmailVerification';
import { useVerifyEmailStatus } from './hooks/useVerifyEmailStatus';

const emailVerifiedMessage = 'Email verified';

const Logger = LoggerFactory.getLogger(DOMAIN.CONSUMER_ENABLEMENT);

export const ResendEmailVerification = ({ sendByDefault }: ResendEmailVerificationProps) => {
  const history = useHistory();
  const [isResendDisabled, setIsResendDisabled] = useState(true);
  const [couldValidateButton, setCouldValidateButton] = useState(false);

  const { data: paymentData } = useMerchantPaymentQuery();
  const { data: lendingApplication } = useCustomerLendingApplication();
  const { data: application } = useProtectedApplication();
  const trackVerificationSuccess = useTrackSendEmailVerification();
  const [
    emailVerificationStorage,
    setEmailVerificationStorage,
  ] = useLocalStorage(EMAIL_VERIFICATION_STATUS, '');
  const [email] = useLocalStorage('email', '');
  const {
    isEmailVerified,
    refetch,
    hasPollingLimitReached,
  } = useIsEmailVerified({ enabled: true, shouldPoll: true });
  const {
    canChangeMessage,
    isManualRefetching,
    verifyEmailStatus,
  } = useVerifyEmailStatus(refetch);
  const {
    mutate: sendEmail,
    data: {
      sendVerificationMessage,
      sendVerificationSecondsLeft,
    } = {},
    error: sendVerificationError,
  } = useSendEmailVerification();

  const customerId = application?.customer_id ?? '0';
  const redirectTo = lendingApplication?.can_continue ? Urls.apply : Urls.home;
  const { countDownHumanize, countDownIsOver, startCounter } = useCountDown(0, 1000, true);
  const resendEmail = useCallback(() => {
    setIsResendDisabled(true);
    sendEmail(customerId);
  }, [sendEmail]);

  useEffect(() => {
    if (sendVerificationSecondsLeft) startCounter(sendVerificationSecondsLeft * 1000);
    if (sendVerificationMessage === emailVerifiedMessage) {
      history.push(Urls.root);
      return;
    }
    setCouldValidateButton(true);
  }, [sendVerificationSecondsLeft, sendVerificationMessage]);

  useEffect(() => {
    if (!sendVerificationError) return;
    setIsResendDisabled(false);
    setCouldValidateButton(false);

    Logger.warn(
      'Error Resend Verification Email',
      `${FEATURE.LOGIN}`,
      {
        error: sendVerificationError?.response?.data?.message,
      },
    );
  }, [sendVerificationError]);

  useEffect(() => {
    if (couldValidateButton) {
      setIsResendDisabled(!countDownIsOver);
    }
  }, [countDownIsOver, couldValidateButton]);

  useEffect(() => {
    if (sendByDefault) {
      if (paymentData && application) {
        sendEmail(customerId);
        trackVerificationSuccess({
          application,
          paymentData,
        });
      }
    } else {
      setIsResendDisabled(false);
    }
  }, [application, paymentData, sendByDefault, customerId, sendEmail, trackVerificationSuccess]);

  useEffect(() => {
    if (isEmailVerified) history.push(redirectTo);
  }, [isEmailVerified, redirectTo]);

  useEffect(() => {
    if (emailVerificationStorage === EMAIL_VERIFICATION_STATUS_VERIFIED) {
      setEmailVerificationStorage('');
      history.push(redirectTo);
    }
  }, [emailVerificationStorage, redirectTo]);

  return (
    <SendEmailUI
      isDefaultMessage={canChangeMessage && isEmailVerified === false}
      email={email}
      onResend={resendEmail}
      onVerified={verifyEmailStatus}
      showVerifiedButton={hasPollingLimitReached}
      resendDisabled={isResendDisabled}
      verifiedDisabled={isManualRefetching}
      countDown={!countDownIsOver ? ` en ${countDownHumanize}` : ''}
    />
  );
};
